/**
 * Save-gate dialog. Surfaces every expression issue grouped by node so
 * the author can fix things before the workflow runs. Clicking an issue
 * selects the offending node in the canvas (the actual "jump to field"
 * is v1 — for now we get the user to the right node).
 *
 * Errors block save; warnings ride along (already-saved). The
 * "Save anyway" path is a second-line escape for authors who knowingly
 * have unresolved refs (e.g. mid-edit content they intend to fill in
 * after wiring up downstream).
 */

import { FC, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { themeColor, xxllncColor } from '../../../layout/theme/color'
import type { NodeIssue, WorkflowValidationResult } from '../autocomplete/validateWorkflow'

interface WorkflowValidationDialogProps {
  open: boolean
  result: WorkflowValidationResult | null
  /** Display name lookup so the dialog doesn't have to know about Node shape. */
  nodeLabel: (nodeId: string) => string
  onCancel: () => void
  onJumpToNode: (nodeId: string) => void
  onSaveAnyway: () => void
}

const WorkflowValidationDialog: FC<WorkflowValidationDialogProps> = ({
  open,
  result,
  nodeLabel,
  onCancel,
  onJumpToNode,
  onSaveAnyway,
}) => {
  const translate = useTranslate()
  const [confirming, setConfirming] = useState<boolean>(false)

  if (!result) return null

  const totalErrors = result.errors.length
  const totalWarnings = result.warnings.length
  const grouped = groupByNode([...result.errors, ...result.warnings])

  const handleSaveAnyway = () => {
    if (confirming) {
      onSaveAnyway()
      setConfirming(false)
    } else {
      setConfirming(true)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        setConfirming(false)
        onCancel()
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorOutlineIcon sx={{ color: xxllncColor.red100 }} />
        {translate('workflow.validation.title')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: themeColor.slate60 }}>
          {translate('workflow.validation.description')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {totalErrors > 0 && (
            <Typography variant="body2" sx={{ color: xxllncColor.red100, fontWeight: 600 }}>
              {translate('workflow.validation.errorCount', {
                count: totalErrors,
                // eslint-disable-next-line camelcase
                smart_count: totalErrors,
              })}
            </Typography>
          )}
          {totalWarnings > 0 && (
            <Typography
              variant="body2"
              sx={{ color: xxllncColor.orange100, fontWeight: 600 }}
            >
              {translate('workflow.validation.warningCount', {
                count: totalWarnings,
                // eslint-disable-next-line camelcase
                smart_count: totalWarnings,
              })}
            </Typography>
          )}
        </Box>
        {result.functionsRegistryUnavailable && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              p: 1,
              mb: 2,
              borderRadius: '6px',
              background: themeColor.slate05,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: themeColor.slate60, mt: 0.25 }} />
            <Typography variant="caption" sx={{ color: themeColor.slate60 }}>
              {translate('workflow.validation.registryUnavailable')}
            </Typography>
          </Box>
        )}
        {grouped.map(({ nodeId, issues }) => (
          <Box
            key={nodeId}
            sx={{
              mb: 1.5,
              border: `1px solid ${themeColor.slate10}`,
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'space-between',
                'p': 1,
                'background': themeColor.slate05,
                'cursor': 'pointer',
                '&:hover': { background: themeColor.slate10 },
              }}
              onClick={() => onJumpToNode(nodeId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onJumpToNode(nodeId)
                }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {nodeLabel(nodeId)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: xxllncColor.blue100, textDecoration: 'underline' }}
              >
                {translate('workflow.validation.jumpToNode')}
              </Typography>
            </Box>
            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {issues.map((issue, idx) => (
                <Box
                  key={`${issue.fieldPath}-${idx}`}
                  sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                >
                  {issue.severity === 'error' ? (
                    <ErrorOutlineIcon sx={{ fontSize: 16, color: xxllncColor.red100, mt: 0.25 }} />
                  ) : (
                    <WarningAmberIcon
                      sx={{ fontSize: 16, color: xxllncColor.orange100, mt: 0.25 }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: themeColor.slate60,
                        fontFamily: 'monospace',
                        display: 'block',
                      }}
                    >
                      {issue.fieldPath}
                    </Typography>
                    <Typography variant="caption">
                      {translate(issue.messageKey, issue.messageArgs ?? {})}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setConfirming(false)
            onCancel()
          }}
          sx={{ textTransform: 'none' }}
        >
          {translate('workflow.validation.cancel')}
        </Button>
        <Button
          color="warning"
          variant={confirming ? 'contained' : 'outlined'}
          onClick={handleSaveAnyway}
          sx={{ textTransform: 'none' }}
        >
          {confirming
            ? translate('workflow.validation.saveAnywayConfirm')
            : translate('workflow.validation.saveAnyway')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const groupByNode = (issues: NodeIssue[]): Array<{ nodeId: string; issues: NodeIssue[] }> => {
  const map = new Map<string, NodeIssue[]>()
  issues.forEach((issue) => {
    const list = map.get(issue.nodeId) ?? []
    list.push(issue)
    map.set(issue.nodeId, list)
  })
  return Array.from(map.entries()).map(([nodeId, list]) => ({ nodeId, issues: list }))
}

export default WorkflowValidationDialog
